import React, { useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ModalReleaseInfo, ListReleasesInfo, LanguageOptions, helpKitSwitcher } from '@ygtechnologies/bygr-frontend-tools';
import { RootStyle } from '@ygtechnologies/rubus-ui';
import NotFound from '@/components/error-pages/NotFound';
import MyProjects from '@/seller/components/project/MyProjects';
import ProfileInvite from '@/components/profile/ProfileInvite';
import ProjectRouting from '@/seller/components/project/ProjectRouting';
import ProfileUserManagement from '@/components/profile/ProfileUserManagement';
import AuthRoute, { AuthRouteConditions } from '@/components/app/AuthRoute';
import SellerListener from './SellerListener';
import UserSettings from '@/components/user-settings/UserSettings';
import SellerSidebar from './components/SellerSidebar';
import { LinkStepWizard } from './components/catalog-groups/LinkToUnit';
import Catalog from './components/catalog/Catalog';
import ProfileEdit from '@/seller/components/profiles/ProfileEdit';
import CreateProject from './components/project/CreateProject';
import ZipFileDownload from './components/documents/ZipFileDownload';
import { useCurrentCulture } from '../utils/hooks';
import SellerReportsPage from './components/reports/SellerReportsPage';
import { ProjectFeatureToggleFlags, useProjectHasFeatureToggle } from '@ygtechnologies/bygr-frontend-component-library';

const changeSettingsOnly: AuthRouteConditions = { profile: 'canChangeProfileSettings' };
const manageUsersOnly: AuthRouteConditions = { profile: 'canManageUsers' };
const profileCanCreateProject: AuthRouteConditions = { profile: 'canCreateProject' };
type SellerRoutingProps = {};

const currentEnvironment = window['APP_CONFIG']['ENVIRONMENT'];
const SellerRouting: React.FC<SellerRoutingProps> = () => {
    let baseUrl = '';
    const showReleaseInformation = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowReleaseInformation);

    const [cultureInfo] = useCurrentCulture();
    const currentLanguage = cultureInfo?.languageCode as LanguageOptions;
    useEffect(() => {
        helpKitSwitcher('rucoria');
    }, []);

    return (
        <>
            <RootStyle value={{ '--cm-sidebar-width': '304px' }} />

            <SellerListener />
            <SellerSidebar />
            {showReleaseInformation && currentLanguage && (
                <ModalReleaseInfo application="CM" environment={currentEnvironment} language={currentLanguage} />
            )}
            <Switch>
                <AuthRoute exact path={`${baseUrl}/user-settings`} component={UserSettings} />
                <AuthRoute
                    path={`${baseUrl}/profile-settings/:tab?`}
                    component={ProfileEdit}
                    conditions={changeSettingsOnly}
                />
                <AuthRoute
                    exact
                    path={`${baseUrl}/profile-users`}
                    component={ProfileUserManagement}
                    conditions={manageUsersOnly}
                />

                <AuthRoute
                    exact
                    path={`${baseUrl}/profile-users/invite`}
                    component={ProfileInvite}
                    conditions={manageUsersOnly}
                />

                <AuthRoute exact path={`${baseUrl}/catalog/:tab(products|groups|linked)/:id?`} component={Catalog} />
                <AuthRoute exact path={`${baseUrl}/catalog/groups/link/:productGroupId`} component={LinkStepWizard} />
                <Redirect exact path={`${baseUrl}/catalog`} to={`${baseUrl}/catalog/products`} />
                <AuthRoute exact path={`${baseUrl}/reports/:reportName`} component={SellerReportsPage} />

                <AuthRoute exact path={`${baseUrl}/projects`} component={MyProjects} />

                <AuthRoute
                    exact
                    path={`${baseUrl}/projects/new`}
                    conditions={profileCanCreateProject}
                    component={CreateProject}
                />
                <AuthRoute path={`${baseUrl}/projects/:projectId`} component={ProjectRouting} />

                <AuthRoute path={`${baseUrl}/download-zip/:downloadId`} component={ZipFileDownload} />

                <AuthRoute
                    exact
                    path={`${baseUrl}/releases`}
                    component={() => (
                        <ListReleasesInfo application="CM" environment={currentEnvironment} language={currentLanguage} />
                    )}
                />

                <Redirect exact path={`${baseUrl}/`} to={`${baseUrl}/projects`} />

                <Route component={NotFound} />
            </Switch>
        </>
    );
};

export default SellerRouting;
